/** Özet matrisi: katalog + hatOzetleri + hatKarar. hatRows kopyalanmaz. */

import { CURRENT_SCENARIO } from "./frequencyScenarios.js";
import {
  compareKararBekleyen,
  HAT_ALT,
  HAT_ANA,
  hatKarar,
  occupancyThreshold,
  qualityViolation,
} from "./freqoptDecision.js";
import { overrideCount } from "./freqoptParams.js";

const ANA_LABEL = {
  [HAT_ANA.YOK]: "Sonuç yok",
  [HAT_ANA.GUNCEL]: "Güncel",
  [HAT_ANA.ESKI]: "Yeniden bakılmalı",
  [HAT_ANA.HATALI]: "Hatalı",
};

const ALT_LABEL = {
  [HAT_ALT.KARAR]: "Karar bekliyor",
  [HAT_ALT.ONERI]: "Öneri hazır",
  [HAT_ALT.ONAYLI]: "Onaylı",
  [HAT_ALT.YAYINDA]: "Yayında",
};

const CURRENT_ID = CURRENT_SCENARIO.id;

export const MATRIX_GROUPS = [
  {
    id: HAT_ALT.KARAR,
    label: "Karar bekleyen",
    defaultCollapsed: false,
    bulkApprove: false,
  },
  {
    id: HAT_ALT.ONERI,
    label: "Öneri hazır",
    defaultCollapsed: false,
    bulkApprove: true,
  },
  {
    id: HAT_ALT.ONAYLI,
    label: "Onaylı",
    defaultCollapsed: true,
    bulkApprove: false,
  },
  {
    id: HAT_ANA.ESKI,
    label: "Eski sonuçlar",
    defaultCollapsed: true,
    bulkApprove: false,
  },
  {
    id: "diger",
    label: "Diğer",
    defaultCollapsed: false,
    bulkApprove: false,
  },
];

export const DEFAULT_COLLAPSED_GROUPS = MATRIX_GROUPS.filter((g) => g.defaultCollapsed).map(
  (g) => g.id
);

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function catalogName(ubsHatList, hatNo) {
  const key = String(hatNo ?? "").trim();
  const hit = (ubsHatList || []).find((h) => String(h?.hat_no ?? "").trim() === key);
  return String(hit?.hat_adi || "").trim();
}

/** Matris satır kaynakları: koşu özeti + durum. Katalog yalnız isim için. */
export function matrixHatNos(hatOzetleri, hatDurumlari) {
  return Array.from(
    new Set([...Object.keys(hatOzetleri || {}), ...Object.keys(hatDurumlari || {})])
  )
    .map((n) => String(n).trim())
    .filter(Boolean);
}

export function scenarioColumnIds(selectedScenarios, hatOzetleri) {
  const fromSel = (selectedScenarios || []).filter((id) => id && id !== CURRENT_ID);
  if (fromSel.length) return fromSel;
  const seen = new Set();
  const out = [];
  Object.values(hatOzetleri || {}).forEach((ozet) => {
    const sira = ozet?.senaryo_sirasi || Object.keys(ozet?.senaryolar || {});
    sira.forEach((id) => {
      if (!id || id === CURRENT_ID || seen.has(id)) return;
      seen.add(id);
      out.push(id);
    });
  });
  return out;
}

export function groupIdForKarar(karar, runDurum) {
  if (karar?.ana === HAT_ANA.ESKI) return HAT_ANA.ESKI;
  if (karar?.ana === HAT_ANA.GUNCEL && karar.alt) return karar.alt;
  if (runDurum === "failed" || karar?.ana === HAT_ANA.HATALI) return "diger";
  return "diger";
}

export function durumLabel(karar, runDurum) {
  if (runDurum === "running") return "işleniyor…";
  if (runDurum === "queued") return "sırada";
  if (karar?.ana === HAT_ANA.HATALI) return ANA_LABEL[HAT_ANA.HATALI];
  if (karar?.ana === HAT_ANA.YOK) return ANA_LABEL[HAT_ANA.YOK];
  const ana = ANA_LABEL[karar?.ana] || "";
  const alt = karar?.alt ? ALT_LABEL[karar.alt] : "";
  if (karar?.ana === HAT_ANA.ESKI) return alt ? `Eski · ${alt}` : "Eski";
  return alt || ana || runDurum || "";
}

function cellOf(blok, params, recommendedId, selectedId, id) {
  if (!blok || blok.hata != null) {
    return {
      id,
      missing: true,
      fark: null,
      farkYuzde: null,
      maliyet: null,
      maxDoluluk: null,
      quality: false,
      recommended: false,
      selected: selectedId === id,
    };
  }
  return {
    id,
    missing: false,
    fark: num(blok.fark_maliyet),
    farkYuzde: num(blok.fark_yuzde),
    maliyet: num(blok.maliyet),
    arac: num(blok.arac),
    sefer: num(blok.sefer),
    maxDoluluk: num(blok.max_doluluk ?? blok.doluluk_profili?.max_doluluk),
    quality: qualityViolation(blok, params),
    recommended: recommendedId === id,
    selected: selectedId === id,
  };
}

export function buildMatrixRow({
  hatNo,
  hatOzetleri,
  hatDurumlari,
  ubsHatList,
  globalParams,
  lineOverrides,
  selectedScenarioByHat,
  approvedHats,
  paramSignatureByHat,
  scenarioIds,
}) {
  const key = String(hatNo).trim();
  const ozet = (hatOzetleri || {})[key] || {};
  const durumRec = (hatDurumlari || {})[key] || {};
  const runDurum = durumRec.durum || ozet.durum || "queued";
  const karar = hatKarar({
    hatNo: key,
    ozet,
    runDurum,
    globalParams,
    lineOverrides,
    selectedScenarioByHat,
    approvedHats,
    storedSignature: (paramSignatureByHat || {})[key],
  });
  const senaryolar = ozet.senaryolar || {};
  const cells = {};
  (scenarioIds || []).forEach((id) => {
    cells[id] = cellOf(senaryolar[id], karar.params, karar.recommendedId, karar.selectedId, id);
  });
  return {
    hat_no: key,
    hat_adi: ozet.hat_adi || durumRec.hat_adi || catalogName(ubsHatList, key) || "",
    durum: runDurum,
    hata: durumRec.hata,
    warn: (ozet.uyari || []).length > 0,
    ozel: overrideCount(lineOverrides, key) > 0,
    karar,
    ana: karar.ana,
    alt: karar.alt,
    senaryolar,
    senaryo_sirasi: ozet.senaryo_sirasi || [],
    maliyet_modeli: ozet.maliyet_modeli,
    mevcut: senaryolar[CURRENT_ID],
    cells,
    maliyetArtiyor: Boolean(karar.maliyetArtiyor),
    groupId: groupIdForKarar(karar, runDurum),
    durumText: durumLabel(karar, runDurum),
  };
}

export function buildMatrixRows(input) {
  const scenarioIds = scenarioColumnIds(input.selectedScenarios, input.hatOzetleri);
  return matrixHatNos(input.hatOzetleri, input.hatDurumlari).map((hatNo) =>
    buildMatrixRow({ ...input, hatNo, scenarioIds })
  );
}

export function isVisited(visitedHats, hatNo) {
  return Boolean((visitedHats || {})[String(hatNo ?? "").trim()]);
}

export function visitStatusOf(row, visitedHats) {
  if (row?.durum === "failed") return "failed";
  if (row?.durum !== "done") return row?.durum || "queued";
  if (row?.karar?.alt === HAT_ALT.YAYINDA || (row?.karar?.publishedAt && row?.karar?.approved && row?.karar?.ana !== HAT_ANA.ESKI)) {
    return "published";
  }
  if (row?.karar?.ana === HAT_ANA.ESKI) return "stale";
  if (row?.karar?.approved) return "approved";
  if (isVisited(visitedHats, row.hat_no)) return "visited";
  return "unvisited";
}

export function visitStatusLabel(row, visitedHats) {
  const id = visitStatusOf(row, visitedHats);
  if (id === "failed") return "Hatalı";
  if (id === "published") return "Yayında";
  if (id === "stale") return "Yeniden bakılmalı";
  if (id === "approved") return "Onaylı";
  if (id === "visited" || id === "unvisited") return "Hazır";
  if (id === "running") return "İşleniyor";
  return "Sırada";
}

export function filterMatrixRows(rows, { search = "", filters = [], visitedHats = {} } = {}) {
  const q = String(search || "").trim().toLowerCase();
  const active = filters || [];
  return (rows || []).filter((row) => {
    if (q && !`${row.hat_no} ${row.hat_adi}`.toLowerCase().includes(q)) return false;
    if (active.includes("warn") && !row.warn) return false;
    if (active.includes("unvisited") && visitStatusOf(row, visitedHats) !== "unvisited") return false;
    return true;
  });
}

function sortGroupRows(groupId, rows) {
  if (groupId === HAT_ALT.KARAR) {
    return [...rows].sort((a, b) => compareKararBekleyen(a.karar, b.karar));
  }
  return [...rows].sort((a, b) =>
    String(a.hat_no).localeCompare(String(b.hat_no), "tr", { numeric: true })
  );
}

export function groupMatrixRows(rows) {
  const byId = Object.fromEntries(MATRIX_GROUPS.map((g) => [g.id, []]));
  (rows || []).forEach((row) => {
    const id = byId[row.groupId] ? row.groupId : "diger";
    byId[id].push(row);
  });
  return MATRIX_GROUPS.map((meta) => ({
    ...meta,
    rows: sortGroupRows(meta.id, byId[meta.id] || []),
  })).filter((g) => g.rows.length > 0 || g.id === HAT_ALT.KARAR || g.id === HAT_ALT.ONERI);
}

/** Karar bekleyen sırası (detay önceki/sonraki). */
export function kararBekleyenHatNos(rows) {
  return sortGroupRows(
    HAT_ALT.KARAR,
    (rows || []).filter((r) => r.groupId === HAT_ALT.KARAR)
  ).map((r) => r.hat_no);
}

export function oneriHazirHatNos(rows) {
  return (rows || []).filter((r) => r.groupId === HAT_ALT.ONERI).map((r) => r.hat_no);
}

/** Onaysız güncel kuyruk: bakılmamış önce, sonra bakılmış. */
export function inboxNavHatNos(rows, visitedHats) {
  const aday = (rows || []).filter(
    (r) =>
      r.durum === "done" &&
      !r.karar?.approved &&
      (r.groupId === HAT_ALT.KARAR || r.groupId === HAT_ALT.ONERI)
  );
  const unvisited = [];
  const visited = [];
  aday.forEach((row) => {
    if (isVisited(visitedHats, row.hat_no)) visited.push(row);
    else unvisited.push(row);
  });
  const sirala = (list) => [
    ...sortGroupRows(
      HAT_ALT.KARAR,
      list.filter((r) => r.groupId === HAT_ALT.KARAR)
    ),
    ...sortGroupRows(
      HAT_ALT.ONERI,
      list.filter((r) => r.groupId === HAT_ALT.ONERI)
    ),
  ];
  return [...sirala(unvisited), ...sirala(visited)].map((r) => r.hat_no);
}

export function nextInboxHat(rows, current, visitedHats) {
  const key = String(current || "");
  const nos = inboxNavHatNos(rows, visitedHats).filter((n) => n !== key);
  const unvisited = nos.filter((n) => !isVisited(visitedHats, n));
  if (unvisited.length) return unvisited[0];
  return nos[0] || null;
}

export function flattenMatrixItems(groups, collapsedIds = []) {
  const collapsed = new Set(collapsedIds || []);
  const items = [];
  (groups || []).forEach((group) => {
    if (!group.rows.length && group.id !== HAT_ALT.KARAR && group.id !== HAT_ALT.ONERI) return;
    items.push({ type: "group", key: `g-${group.id}`, group });
    if (collapsed.has(group.id)) return;
    group.rows.forEach((row) => {
      items.push({ type: "row", key: `h-${row.hat_no}`, row, groupId: group.id });
    });
  });
  return items;
}

export function filterChipCounts(rows, visitedHats) {
  const warned = (rows || []).filter((r) => r.warn).length;
  const unvisited = (rows || []).filter((r) => visitStatusOf(r, visitedHats) === "unvisited").length;
  return [
    { id: "warn", label: "Uyarılı", count: warned },
    { id: "unvisited", label: "Bakılmadı", count: unvisited },
  ].filter((chip) => chip.count > 0);
}

/** Özet kuyruk sekmeleri: inceleme / onaylı / tümü. */
export function summaryQueueCounts(rows) {
  const list = rows || [];
  let review = 0;
  let approved = 0;
  let published = 0;
  let approvedUnpublished = 0;
  list.forEach((r) => {
    if (r?.durum !== "done") return;
    if (r?.karar?.alt === HAT_ALT.YAYINDA || (r?.karar?.publishedAt && r?.karar?.approved)) {
      published += 1;
    }
    if (r?.karar?.approved) {
      approved += 1;
      if (r?.karar?.alt !== HAT_ALT.YAYINDA && !r?.karar?.publishedAt) {
        approvedUnpublished += 1;
      }
      return;
    }
    if (r.groupId === HAT_ALT.KARAR || r.groupId === HAT_ALT.ONERI) review += 1;
  });
  return {
    all: list.length,
    review,
    approved,
    published,
    approvedUnpublished,
  };
}

export function filterRowsByQueueTab(rows, tabId) {
  const list = rows || [];
  if (tabId === "review") {
    return list.filter(
      (r) =>
        r.durum === "done" &&
        !r.karar?.approved &&
        (r.groupId === HAT_ALT.KARAR || r.groupId === HAT_ALT.ONERI)
    );
  }
  if (tabId === "approved") {
    return list.filter((r) => r?.karar?.approved);
  }
  return list;
}

export function neighborKararHat(list, current, dir) {
  const nos = list || [];
  if (!nos.length) return null;
  const key = String(current || "");
  const idx = nos.indexOf(key);
  if (idx < 0) return dir > 0 ? nos[0] : nos[nos.length - 1];
  return nos[idx + dir] || null;
}

function blokOfSonuc(sonuc, scenarioId) {
  return (sonuc?.senaryolar || {})[scenarioId] || null;
}

/**
 * Saat kırılımı yalnız tam GET sonucundan.
 * Özetten saat uydurulmaz; saatlik yoksa boş dizi.
 */
export function qualityHoursFromSonuc(sonuc, scenarioId, params) {
  const blok = blokOfSonuc(sonuc, scenarioId);
  if (!blok) return [];
  const threshold = occupancyThreshold(params);
  const profil = blok.doluluk_profili || {};
  const saatlik = Array.isArray(profil.saatlik) ? profil.saatlik : [];
  const hits = saatlik.filter((s) => {
    const max = Number(s?.max_doluluk);
    return Number.isFinite(max) && max > threshold + 1e-9;
  });
  if (hits.length) {
    return hits.map((s) => ({
      scenarioId,
      saat: s.saat,
      yon: s.yon,
      max_doluluk: s.max_doluluk,
      sefer: s.sefer,
    }));
  }
  const yer = profil.max_doluluk_yeri;
  const max = num(blok.max_doluluk ?? profil.max_doluluk);
  if (yer && max != null && max > threshold + 1e-9) {
    return [
      {
        scenarioId,
        saat: yer.saat,
        yon: yer.yon,
        max_doluluk: max,
        sefer: null,
      },
    ];
  }
  return [];
}

